import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams, useSearchParams } from 'react-router';
import { DocumentsTab } from '../../components/product/DocumentsTab';
import { FormulationTab } from '../../components/product/FormulationTab';
import { SupplementFactsTab } from '../../components/product/SupplementFactsTab';
import { WorkflowTab } from '../../components/product/WorkflowTab';
import { Alert, Badge, Button, Card, Field, Input } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { Document, Product, Task } from '../../lib/types';

type Tab = 'documents' | 'formulation' | 'workflow' | 'sfp';

export function ProductPage() {
  const { t } = useTranslation();
  const { pid } = useParams();
  const { identity } = useSession();
  const orgId = identity?.org?.id;
  const [q, setQ] = useSearchParams();
  const tab = (q.get('tab') as Tab) || 'documents';
  const roles = identity?.roles ?? [];
  const sa = !!identity?.superAdmin;
  // Support mode (Section 16.3): uploads/checks need the org's Team Space
  const isFunctional = sa || roles.some((r) => ['admin', 'ra', 'qa', 'design'].includes(r));
  const canUpload = !identity?.supportMode && isFunctional;
  const canEditProduct = sa || roles.some((r) => ['admin', 'ra', 'design'].includes(r));
  const canEditFormulation = sa || roles.some((r) => ['admin', 'ra'].includes(r));
  const isAdmin = sa || roles.includes('admin');
  const [product, setProduct] = useState<Product | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({
    name: '',
    description: '',
    dosageForm: '',
    servingAmount: '',
    servingUnit: '',
    servingsPerContainer: '',
    launchTargetDate: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const base = `/api/v1/orgs/${orgId}/products/${pid}`;

  const load = useCallback(async () => {
    if (!orgId) return;
    const [p, tk, docs] = await Promise.all([
      api<Product>(base),
      api<{ tasks: Task[] }>(`${base}/tasks`),
      api<{ documents: Document[] }>(`${base}/documents`),
    ]);
    setProduct(p);
    setTasks(tk.tasks);
    setDocuments(docs.documents);
    setF({
      name: p.name,
      description: p.description,
      dosageForm: p.dosageForm ?? '',
      servingAmount: p.servingSize ? String(p.servingSize.amount) : '',
      servingUnit: p.servingSize?.unit ?? '',
      servingsPerContainer: p.servingsPerContainer ? String(p.servingsPerContainer) : '',
      launchTargetDate: p.launchTargetDate ?? '',
    });
  }, [base, orgId]);
  useEffect(() => {
    void load().catch((e: unknown) => setErr(e instanceof ApiError ? e.message : String(e)));
  }, [load]);
  if (identity && identity.state !== 'member') return <Navigate to="/" replace />;
  if (!product) return err ? <Alert>{err}</Alert> : <p className="text-slate-500">{t('app.loading')}</p>;

  const saveDetails = async () => {
    setErr(null);
    try {
      await api(base, {
        method: 'PATCH',
        json: {
          name: f.name,
          description: f.description,
          dosageForm: f.dosageForm || null,
          servingSize:
            f.servingAmount && f.servingUnit
              ? { amount: Number(f.servingAmount), unit: f.servingUnit }
              : null,
          servingsPerContainer: f.servingsPerContainer ? Number(f.servingsPerContainer) : null,
          launchTargetDate: f.launchTargetDate || null,
        },
      });
      setEditing(false);
      await load();
    } catch (e) {
      setErr(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e));
    }
  };
  const archive = async (archived: boolean) => {
    await api(`${base}/archive`, { method: 'POST', json: { archived } });
    await load();
  };
  const TabBtn = ({ id, label }: { id: Tab; label: string }) => (
    <button
      className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === id ? 'bg-emerald-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}
      onClick={() => setQ({ tab: id })}
    >
      {label}
    </button>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/products" className="text-sm text-emerald-700 underline">
          ← {t('products.title')}
        </Link>
        <h1 className="text-2xl font-semibold">{product.name}</h1>
        <span className="font-mono text-sm text-slate-500">{product.id}</span>
        <Badge tone={product.status === 'active' ? 'green' : 'slate'}>
          {t(`products.${product.status}`)}
        </Badge>
        <div className="ml-auto flex gap-2">
          {canEditProduct && product.status === 'active' && (
            <Button variant="secondary" onClick={() => setEditing((v) => !v)}>
              {t('products.edit')}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant={product.status === 'active' ? 'danger' : 'secondary'}
              onClick={() => void archive(product.status === 'active')}
            >
              {product.status === 'active' ? t('products.archive') : t('products.restore')}
            </Button>
          )}
        </div>
      </div>
      {err && <Alert>{err}</Alert>}
      {editing && (
        <Card title={t('products.edit')}>
          <div className="grid gap-3 md:grid-cols-3">
            <Field label={t('products.name')}>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            </Field>
            <Field label={t('products.description')}>
              <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            </Field>
            <Field label={t('products.dosageForm')}>
              <Input
                value={f.dosageForm}
                onChange={(e) => setF({ ...f, dosageForm: e.target.value })}
                placeholder="stick / capsule / tablet"
              />
            </Field>
            <Field label={t('products.servingSize')}>
              <div className="flex gap-2">
                <Input
                  type="number"
                  step="any"
                  value={f.servingAmount}
                  onChange={(e) => setF({ ...f, servingAmount: e.target.value })}
                  className="w-24"
                />
                <Input
                  value={f.servingUnit}
                  onChange={(e) => setF({ ...f, servingUnit: e.target.value })}
                  placeholder="stick (3 g)"
                />
              </div>
            </Field>
            <Field label={t('products.servingsPerContainer')}>
              <Input
                type="number"
                value={f.servingsPerContainer}
                onChange={(e) => setF({ ...f, servingsPerContainer: e.target.value })}
              />
            </Field>
            <Field label={t('products.launchTargetDate')}>
              <Input
                type="date"
                value={f.launchTargetDate}
                onChange={(e) => setF({ ...f, launchTargetDate: e.target.value })}
              />
            </Field>
          </div>
          <div className="mt-3 flex gap-2">
            <Button onClick={() => void saveDetails()}>{t('products.save')}</Button>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              {t('products.cancel')}
            </Button>
          </div>
        </Card>
      )}
      <p className="text-sm text-slate-600">
        {product.description}
        <span className="ml-3 text-xs text-slate-400">
          {t('products.folder')}:{' '}
          <span className="font-mono">{product.directoryId ?? t('products.folderMissing')}</span>
        </span>
      </p>
      <nav className="flex gap-1 border-b border-slate-200 pb-2">
        <TabBtn id="documents" label={`${t('products.tabs.documents')} (${documents.length})`} />
        <TabBtn id="formulation" label={t('products.tabs.formulation')} />
        <TabBtn id="workflow" label={t('products.tabs.workflow')} />
        <TabBtn id="sfp" label={t('products.tabs.sfp')} />
      </nav>
      {tab === 'documents' && orgId && pid && (
        <DocumentsTab
          orgId={orgId}
          pid={pid}
          canUpload={canUpload && product.status === 'active'}
          tasks={tasks}
          onChanged={() => void load()}
        />
      )}
      {tab === 'formulation' && orgId && pid && (
        <FormulationTab
          orgId={orgId}
          pid={pid}
          canEdit={canEditFormulation && product.status === 'active'}
          canAccept={isFunctional}
          documents={documents}
          onChanged={() => void load()}
        />
      )}
      {tab === 'workflow' && <WorkflowTab tasks={tasks} />}
      {tab === 'sfp' && orgId && pid && (
        <SupplementFactsTab
          orgId={orgId}
          pid={pid}
          canVerify={canEditFormulation && product.status === 'active'}
          canOverride={isAdmin || roles.includes('ra')}
          canAccept={isFunctional}
        />
      )}
    </div>
  );
}
